import { storage } from '@modules/firebase';
import { generateEntityId } from '@modules/helpers';
import {
  type StorageError,
  type UploadMetadata,
  type UploadTask,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';

import { logger } from './logger';
import { removePortFromUrl } from './url-helpers';

function getTimeoutSignal(timeout?: number): AbortSignal | undefined {
  if (typeof timeout === 'number' && typeof AbortSignal?.timeout === 'function') {
    return AbortSignal.timeout(timeout);
  }
  return undefined;
}

const DEFAULT_USER_STORAGE_PATH = 'bb_usr_files/uploaded_images';

type UploadParams = {
  storagePath: string;
  id?: string;
  cache?: boolean;
};

interface ImageUploadStatusCallbacks {
  onProgressUpdate?: (progress: number) => void;
  onError?: (error: StorageError) => void;
  onSuccess: (url: string, id: string) => void;
}

export async function uploadImage(
  imageReference: string | undefined,
  { onProgressUpdate, onError, onSuccess }: ImageUploadStatusCallbacks,
  storagePath = DEFAULT_USER_STORAGE_PATH,
  timeout?: number,
): Promise<void> {
  if (!imageReference) return;

  try {
    const response = await fetch(imageReference, {
      signal: getTimeoutSignal(timeout),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const blob = await response.blob();
    uploadToDB(blob, { onProgressUpdate, onError, onSuccess }, { storagePath, cache: true });
  } catch (error) {
    logger.error('Failed to fetch image for upload', {
      imageReference,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    onError?.(error as StorageError);
  }
}

/**
 * Upload a base64 image which can be cancellable at any time
 * @param imageReference The base64 image url
 * @params callbacks callbacks such as onError, onProgressUpdate and onSuccess
 * @param folderName The folder name you wan to upload to
 * @param imageId The id of the image id
 * @returns The function that can be used to cancel the upload task
 */
export function cancelableUploadBase64ImageTask(
  imageReference: string | undefined,
  callbacks: ImageUploadStatusCallbacks,
  folderName = DEFAULT_USER_STORAGE_PATH,
  imageId?: string,
): (() => void) | undefined {
  if (!imageReference) return;
  const blob = dataURLtoBlob(imageReference);
  const uploadTask = uploadToDB(
    blob,
    { ...callbacks },
    { storagePath: folderName ?? '', id: imageId, cache: true },
  );

  function cancelableUploadTask() {
    uploadTask.cancel();
  }

  return cancelableUploadTask;
}

interface ImageUploadPromiseStatusCallbacks {
  onProgressUpdate?: (progress: number) => void;
}

interface ImageUploadPromiseStatusCallbacks {
  onProgressUpdate?: (progress: number) => void;
}

export async function uploadImageAsync(
  imageReference: string | undefined | null,
  callbacks?: ImageUploadPromiseStatusCallbacks,
  folderName = DEFAULT_USER_STORAGE_PATH,
  imageId?: string,
  timeout?: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!imageReference) {
      logger.error('No image reference');
      return reject('No image reference');
    }

    return fetch(imageReference, {
      signal: getTimeoutSignal(timeout),
    })
      .then((r: Response) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
        return r.blob();
      })
      .then((blob: Blob) => {
        uploadToDB(
          blob,
          {
            onProgressUpdate: callbacks?.onProgressUpdate,
            onError: (error) => {
              logger.error('Firebase storage returned an error when uploading the image', {
                error,
              });
              return reject(error);
            },
            onSuccess: (url) => resolve(url),
          },
          { storagePath: folderName, cache: true, id: imageId },
        );
      })
      .catch((e) => {
        logger.error('Error when uploading the image', { error: e });
        return reject(e);
      });
  });
}

export async function uploadImageBlobAsync(
  blob: Blob,
  callbacks?: ImageUploadPromiseStatusCallbacks,
  folderName = DEFAULT_USER_STORAGE_PATH,
  imageId?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    uploadToDB(
      blob,
      {
        onProgressUpdate: callbacks?.onProgressUpdate,
        onError: (error) => {
          logger.error('Firebase storage returned an error when uploading the image blob', {
            error,
          });
          reject(error);
        },
        onSuccess: (url) => {
          resolve(url);
        },
      },
      {
        storagePath: folderName,
        cache: true,
        id: imageId,
      },
    );
  });
}

const uploadToDB = (
  blob: Blob,
  { onProgressUpdate, onError, onSuccess }: ImageUploadStatusCallbacks,
  { storagePath, id, cache = false }: UploadParams,
): UploadTask => {
  // You always want to generate a new ID at this point. We still allow users to cancel any changes made
  // after uploading the image, and if we had overwritten their existing image we wouldn't be able to undo
  // that change.
  const imageId = id || generateEntityId();
  const fileExtension = blob.type.split('image/')[1];
  const fileName = fileExtension ? `${imageId}.${fileExtension}` : imageId;
  const storageRef = ref(storage, `${storagePath}/${fileName}`);

  // By setting the cache control we make it so that the browser can serve the image from the disk cache for
  // 1 year before having to refetch it. This is fine because any adjustment to the image actually creates a
  // new image so there shouldn't ever be a situation where you serve a stale image
  const metadata: UploadMetadata = {};
  if (cache) metadata.cacheControl = 'public, max-age=31536000';
  const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      onProgressUpdate?.(progress);
    },
    (error) => {
      onError?.(error);
    },
    () => {
      // Handle successful upload
      getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
        onSuccess(downloadURL, imageId);
      });
    },
  );

  return uploadTask;
};

export function getBoundedDimensions(
  image: HTMLImageElement | undefined,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  let width = 0;
  let height = 0;
  const maxRatio = maxWidth / maxHeight;

  if (!image) return { width, height };

  if (image.naturalWidth < image.naturalHeight * maxRatio) {
    if (image.naturalHeight < maxHeight) {
      // If the image is taller than it is wider but it is shorter than the max height
      width = image.naturalWidth;
      height = image.naturalHeight;
    } else {
      // If the image is taller than it is wider and it is taller than the maximum allowed height
      height = maxHeight;
      width = (maxHeight / image.naturalHeight) * image.naturalWidth;
    }
  } else if (image.naturalWidth >= image.naturalHeight * maxRatio) {
    if (image.naturalWidth < maxWidth) {
      // If the image is wider than it is tall (or perfectly square) but it is shorter than the max width
      width = image.naturalWidth;
      height = image.naturalHeight;
    } else {
      // If the image is wider than it is tall (or perfectly square) and it is wider than the maximum allowed width
      width = maxWidth;
      height = (maxWidth / image.naturalWidth) * image.naturalHeight;
    }
  }

  return { width: Math.max(width, 1), height: Math.max(height, 1) }; // never return <1 dimension
}

export async function downloadImageBlob(url: string, timeout?: number): Promise<Blob> {
  try {
    const response = await fetch(url, {
      signal: getTimeoutSignal(timeout),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.blob();
  } catch (error) {
    logger.error('Failed to download image blob', {
      url,
      timeout,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: error instanceof Error ? error.constructor.name : 'Unknown',
    });

    throw error;
  }
}

/** Downloads an image from firebase to a browser blob */
export function downloadRemoteImage(
  imageUrl: string | undefined,
  timeout?: number,
): Promise<Blob> | undefined {
  if (imageUrl) {
    // refFromURL fails when you are running in localhost due to a bug in the firebase SDK. This has been fixed in v9 however
    // we haven't upgraded so for now we workaround the issue by fetching the image if it's a local URL. This is fine since we currently
    // store the full URL to the image (including the token) so refFromURL isn't strictly needed.
    // https://github.com/firebase/firebase-tools/issues/3715
    if (
      imageUrl.startsWith('blob:') ||
      imageUrl.startsWith('data:') ||
      imageUrl.startsWith('http://localhost')
    ) {
      return downloadImageBlob(imageUrl, timeout);
    }

    const cleanedImageUrl = removePortFromUrl(imageUrl);
    const pathReference = ref(storage, cleanedImageUrl);

    // Extra temporary performance optimisation: We currently store download URLs in our firestore card documents
    // (which isn't great) but since we already have it it's a waste to go and fetch the download URL again. So we
    // detect if it is a download URL (by checking if a token exists on the URL) and if so we bypass this step.
    const isDownloadURL = (() => {
      try {
        return new URL(cleanedImageUrl).searchParams.has('token');
      } catch {
        return false;
      }
    })();
    if (isDownloadURL) {
      return downloadImageBlob(cleanedImageUrl, timeout);
    } else {
      return getDownloadURL(pathReference)
        .then((url) => {
          return downloadImageBlob(url, timeout);
        })
        .catch((err) => {
          // propagate error for test and caller
          throw err;
        });
    }
  }
}

export function createImageElementFromURL(
  logoUrl: string | undefined,
): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve) => {
    if (!logoUrl) {
      resolve(undefined);
      return;
    }

    const img = new Image();

    img.onload = () => {
      resolve(img);
    };

    img.src = logoUrl;
  });
}

export function getDataUrlFromImageBlob(logoBlob: Blob | undefined): Promise<string | undefined> {
  return new Promise((resolve) => {
    if (!logoBlob) {
      resolve(undefined);
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      }
    };

    reader.readAsDataURL(logoBlob);
  });
}

export function saveImageBlobToDisk({ blob, fileName }: { blob: Blob; fileName: string }): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
}

export function dataURLtoBlob(dataurl: string): Blob {
  const arr = dataurl.split(',');

  if (arr.length < 2)
    throw new Error('Data URL is not the correct format, e.g. data:image/png;base64,');
  const mimeTypeMatch = arr[0].match(/:(.*?);/);
  if (!mimeTypeMatch) throw new Error('Data URL is missing the MIME type');

  const mime = mimeTypeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}

export function getImageDimensions(fileOrSrc: File | string): Promise<[number, number]> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve([img.naturalHeight, img.naturalWidth]);
    img.onerror = reject;
    if (typeof fileOrSrc === 'string') {
      img.src = fileOrSrc;
    } else {
      const fileLoader = new FileReader();
      fileLoader.onload = function () {
        const data = this.result;

        if (typeof data === 'string') {
          img.src = data;
        }
      };
      fileLoader.readAsDataURL(fileOrSrc);
    }
  });
}

export function resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
  const fileLoader = new FileReader();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const imageObj = new Image();

  if (!context) throw new Error('Could not get canvas context');

  //create a hidden canvas object we can use to create the new resized image data
  canvas.id = 'hiddenCanvas';
  canvas.style.visibility = 'hidden';
  canvas.style.position = 'absolute';
  canvas.style.left = '-9999cm';

  return new Promise((resolve, reject) => {
    // check for an image then
    //trigger the file loader to get the data from the image
    if (file.type.match('image.*')) {
      fileLoader.readAsDataURL(file);
    } else {
      reject('File is not an image');
    }

    // setup the file loader onload function
    // once the file loader has the data it passes it to the
    // image object which, once the image has loaded,
    // triggers the images onload function
    fileLoader.onload = function () {
      const data = this.result;

      if (typeof data === 'string') {
        imageObj.src = data;
      }
    };

    fileLoader.onabort = function () {
      reject('The upload was aborted.');
    };

    fileLoader.onerror = function () {
      reject('An error occured while reading the file.');
    };

    // set up the images onload function which clears the hidden canvas context,
    // draws the new image then gets the blob data from it
    imageObj.onload = function () {
      // Check for empty images
      if (imageObj.width == 0 || imageObj.height == 0) {
        reject('Image is empty');
      } else {
        const resizeDimensions = getBoundedDimensions(imageObj, maxWidth, maxHeight);
        document.body.appendChild(canvas);
        canvas.width = resizeDimensions.width;
        canvas.height = resizeDimensions.height;
        context.clearRect(0, 0, resizeDimensions.width, resizeDimensions.height);
        context.drawImage(
          imageObj,
          0,
          0,
          imageObj.width,
          imageObj.height,
          0,
          0,
          resizeDimensions.width,
          resizeDimensions.height,
        );

        const blob = dataURLtoBlob(canvas.toDataURL(file.type));

        document.body.removeChild(canvas);
        //pass this blob to your upload function
        resolve(blob);
      }
    };

    imageObj.onabort = function () {
      reject('Image load was aborted.');
    };

    imageObj.onerror = function () {
      reject('An error occured while loading image.');
    };
  });
}
